import React, { useEffect } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { Header } from './components/Header';
import { Hero } from './components/Hero';
import { FeaturesSection } from './components/FeaturesSection';
import { HowItWorks } from './components/HowItWorks';
import { CtaBand } from './components/CtaBand';
import { Faq } from './components/Faq';
import { Footer } from './components/Footer';
import { EventsMapSection } from './components/map/EventsMapSection';
import { Reveal } from './components/Reveal';
import { PhotoMarquee } from './components/PhotoMarquee';
import { CinematicBand } from './components/CinematicBand';
import { ParallaxCollage } from './components/ParallaxCollage';
import { EventPage } from './pages/EventPage';
import { GetApp } from './pages/GetApp';
import { Privacy } from './pages/Privacy';
import { Terms } from './pages/Terms';
import { Community } from './pages/Community';
import { WhatsNew } from './pages/WhatsNew';
import { LocaleProvider, useLocale } from './i18n/LocaleContext';
import { SEO_COPY } from './i18n/landing';
import { useSeo } from './seo/useSeo';
import { analytics, WebAnalyticsEvent } from './analytics/analyticsService';
import { useScrollDepth } from './analytics/useScrollDepth';
import { useSmoothScroll } from './hooks/useSmoothScroll';
import { DeleteAccountPage } from './pages/DeleteAccountPage';
import { AdminLogin } from './pages/AdminLogin';
import { AdminDashboard } from './pages/AdminDashboard';
import { AdminUsers } from './pages/AdminDashboard/Users';
import { AdminUserDetailPage } from './pages/AdminDashboard/UserDetail';
import { AdminEvents } from './pages/AdminDashboard/Events';
import { AdminEventDetailPage } from './pages/AdminDashboard/EventDetail';
import { AdminClubs } from './pages/AdminDashboard/Clubs';
import { AdminClubDetailPage } from './pages/AdminDashboard/ClubDetail';
import { AdminAppSettings } from './pages/AdminDashboard/AppSettings';
import { AdminCandidates } from './pages/AdminDashboard/Candidates';
import { AdminCandidateDetailPage } from './pages/AdminDashboard/CandidateDetail';
import { AdminCovers } from './pages/AdminDashboard/Covers';
import { AdminWhatsNew } from './pages/AdminDashboard/WhatsNewAdmin';
import { AdminPayouts } from './pages/AdminDashboard/Payouts';
import { AdminAnalytics } from './pages/AdminDashboard/Analytics';
import { Navigate } from 'react-router-dom';
import './styles/landing.css';
import './styles/landing-fresh.css';
import './styles/landing-neon.css';

function LandingInner(): React.JSX.Element {
  const { locale } = useLocale();
  const copy = SEO_COPY[locale];

  useScrollDepth();
  useSmoothScroll();

  // Initialize analytics and report the landing locale. init() is internally
  // memoized, so even though this effect re-runs on locale change the readiness
  // check happens once; awaiting it ensures the very first language_view isn't
  // dropped before analytics is ready. Fire-and-forget — never blocks render.
  useEffect(() => {
    void analytics.init().then(() => {
      analytics.logEvent(WebAnalyticsEvent.LANGUAGE_VIEW, { locale });
    });
  }, [locale]);

  useSeo({
    title: copy.title.replace(' — Gather', '').replace('Gather — ', ''),
    description: copy.description,
    canonical: '/',
    ogTitle: copy.title,
    ogDescription: copy.description,
    locale: copy.ogLocale,
  });

  return (
    <div className="landing landing--neon" id="top">
      <Header />
      <main>
        <Hero />
        <PhotoMarquee />
        <Reveal><FeaturesSection /></Reveal>
        <CinematicBand />
        <ParallaxCollage />
        <Reveal><HowItWorks /></Reveal>
        <Reveal><EventsMapSection /></Reveal>
        <Reveal><CtaBand /></Reveal>
        <Reveal><Faq /></Reveal>
      </main>
      <Footer />
    </div>
  );
}

function Landing(): React.JSX.Element {
  return (
    <LocaleProvider>
      <LandingInner />
    </LocaleProvider>
  );
}

function App(): React.JSX.Element {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<Landing />} />
        <Route path="/event/:id" element={<EventPage />} />
        <Route path="/get" element={<GetApp />} />
        <Route path="/privacy" element={<Privacy />} />
        <Route path="/terms" element={<Terms />} />
        <Route path="/community" element={<Community />} />
        <Route path="/whats-new" element={<WhatsNew />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />
        <Route path="/admin" element={<AdminLogin />} />
        <Route path="/admin/dashboard" element={<AdminDashboard />}>
          <Route index element={<Navigate to="users" replace />} />
          <Route path="users" element={<AdminUsers />} />
          <Route path="users/:id" element={<AdminUserDetailPage />} />
          <Route path="events" element={<AdminEvents />} />
          <Route path="events/:id" element={<AdminEventDetailPage />} />
          <Route path="candidates" element={<AdminCandidates />} />
          <Route
            path="candidates/:id"
            element={<AdminCandidateDetailPage />}
          />
          <Route path="clubs" element={<AdminClubs />} />
          <Route path="clubs/:id" element={<AdminClubDetailPage />} />
          <Route path="covers" element={<AdminCovers />} />
          <Route path="whats-new" element={<AdminWhatsNew />} />
          <Route path="payouts" element={<AdminPayouts />} />
          <Route path="analytics" element={<AdminAnalytics />} />
          <Route path="app" element={<AdminAppSettings />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
}

export default App;
